import type { PspUnion } from '@payments/types'
import { type Ref, useRoute, useState, useUserInfo, whenever } from '#imports'

export interface DepositReceiptTransaction {
    txId: string
    psp: PspUnion
    isDismissed: boolean
}

interface UseDepositReceiptOptions {
    /** For the component that renders the receipt: the pending page and PSP returns hand the transaction over in the URL. */
    followRoute?: boolean
}

export interface UseDepositReceiptReturn {
    receiptTransaction: Ref<DepositReceiptTransaction | null>
    showReceipt: (txId: string, psp: PspUnion) => void
    dismissReceipt: () => void
}

export const useDepositReceipt = (options: UseDepositReceiptOptions = {}): UseDepositReceiptReturn => {
    // Shared state, not the URL: an in-page confirmation shares the success broadcast with receivers that rewrite
    // the whole query (playgame's togglePayments, the deposit app's resetView), so a txId in the URL can be gone
    // before the receipt has read it
    const receiptTransaction = useState<DepositReceiptTransaction | null>('deposit:receipt-transaction', () => null)

    function showReceipt(txId: string, psp: PspUnion) {
        // A transaction can arrive more than once (the broadcast and the URL it writes, or a remounted overlay
        // reading the URL again); that must not bring back a receipt the player already closed
        if (receiptTransaction.value?.txId === txId) return

        receiptTransaction.value = { txId, psp, isDismissed: false }
    }

    // Kept in shared state, not in the receipt: a layout change (e.g. into the deposit page's minimal layout)
    // remounts the overlay and with it the receipt
    function dismissReceipt() {
        if (!receiptTransaction.value) return

        receiptTransaction.value = { ...receiptTransaction.value, isDismissed: true }
    }

    if (options.followRoute) {
        const route = useRoute()
        const { isLoggedIn } = useUserInfo()

        whenever(() => isLoggedIn.value && !!route.query.txId && !!route.query.psp, () => {
            showReceipt(route.query.txId as string, route.query.psp as PspUnion)
        }, { immediate: true })
    }

    return {
        receiptTransaction,
        showReceipt,
        dismissReceipt,
    }
}
